import { Typography } from "@mui/material"
import type { BiomeshContactResponse } from "@phc/common/gen/threatAnalyticsGen"
import {
  getListContactsBiomeshContactListGetQueryKey,
  useDeleteContactBiomeshContactContactIdDelete,
} from "@phc/common/gen/threatAnalyticsGen"
import { useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import type { FC } from "react"
import { useCallback } from "react"
import { Modal } from "../Shared/Modal"
import { extractApiError } from "./copUtils"

interface DeleteContactDialogProps {
  open: boolean
  onClose: () => void
  contact: BiomeshContactResponse
}

export const DeleteContactDialog: FC<DeleteContactDialogProps> = ({
  open,
  onClose,
  contact,
}) => {
  const queryClient = useQueryClient()
  const { mutateAsync: deleteContact, isPending } =
    useDeleteContactBiomeshContactContactIdDelete()

  const handleConfirm = useCallback(async () => {
    try {
      await deleteContact({ contactId: contact.contact_id })
      await queryClient.invalidateQueries({
        queryKey: getListContactsBiomeshContactListGetQueryKey(),
      })
      enqueueSnackbar("Contact deleted", { variant: "success" })
      onClose()
    } catch (err) {
      enqueueSnackbar(extractApiError(err, "Failed to delete contact"), {
        variant: "error",
      })
    }
  }, [deleteContact, contact.contact_id, queryClient, onClose])

  return (
    <Modal
      open={open}
      onClose={onClose}
      onConfirm={() => void handleConfirm()}
      title="Delete Contact"
      confirmLabel="Delete"
      disableBtns={isPending}
    >
      <Typography sx={{ mt: 1 }}>
        Are you sure you want to delete{" "}
        <strong>
          {contact.first_name} {contact.last_name}
        </strong>
        ?
      </Typography>
    </Modal>
  )
}
